/**
 * Step timeline from `GET /api/jobs/{id}/steps` (supervisor and above).
 *
 * Shows when, which phase, which turn, the step type and the action
 * kind. It deliberately does not render typed text, selectors,
 * coordinates or error text: the API returns those unredacted and they
 * can carry patient data or portal content. Supervisors who need them
 * open the replay.
 */
import { useLiveJobSteps, type PhaseDetail } from "./jobApi";
import { DeniedNotice, ErrorNotice, Panel, Skeleton } from "./parts";
import { isStatus } from "./ui";
import { fmtDateTime } from "@/lib/format";

interface Props {
  jobId: string;
  phases: PhaseDetail[];
  /** Keep polling while the job is not finished. */
  live: boolean;
}

function typeLabel(stepType: string): string {
  const text = stepType.replace(/_/g, " ");
  return text.charAt(0).toUpperCase() + text.slice(1);
}

export function StepTimeline({ jobId, phases, live }: Props) {
  const query = useLiveJobSteps(jobId, live);
  const phaseName = new Map(phases.map((p) => [p.id, `${p.order}. ${p.name}`]));
  const steps = query.data?.items ?? [];

  let body: React.ReactNode;
  if (query.isPending) {
    body = (
      <div role="status" aria-busy="true" className="space-y-2">
        <span className="sr-only">Loading steps</span>
        {[0, 1, 2, 3].map((i) => (
          <Skeleton key={i} className="h-6" />
        ))}
      </div>
    );
  } else if (isStatus(query.error, 403)) {
    body = (
      <DeniedNotice
        what="step details"
        roles="supervisor, entity admin, compliance officer or investigator"
      />
    );
  } else if (query.isError) {
    body = (
      <ErrorNotice
        message="Steps could not be loaded."
        error={query.error}
        onRetry={() => query.refetch()}
      />
    );
  } else if (steps.length === 0) {
    body = (
      <p className="text-sm text-ink-3">
        No steps recorded. A full run records one step per agent turn. A smoke run records none.
      </p>
    );
  } else {
    body = (
      <>
        <table className="w-full text-left text-xs">
          <thead>
            <tr className="border-b border-paper-line text-[11px] uppercase tracking-wider text-ink-3">
              <th className="py-1.5 pr-3 font-medium">Time</th>
              <th className="py-1.5 pr-3 font-medium">Phase</th>
              <th className="py-1.5 pr-3 font-medium">Turn</th>
              <th className="py-1.5 pr-3 font-medium">Type</th>
              <th className="py-1.5 pr-3 font-medium">Action</th>
              <th className="py-1.5 font-medium">Flags</th>
            </tr>
          </thead>
          <tbody>
            {steps.map((s) => {
              const flags = [s.tookRetry ? "Retried" : null, s.errorMessage ? "Error" : null]
                .filter(Boolean)
                .join(", ");
              return (
                <tr key={s.id} className="border-b border-paper-line last:border-0">
                  <td className="py-1.5 pr-3 font-mono text-ink-3">{fmtDateTime(s.occurredAt)}</td>
                  <td className="py-1.5 pr-3 text-ink-1">
                    {(s.phaseId && phaseName.get(s.phaseId)) || "—"}
                  </td>
                  <td className="py-1.5 pr-3 font-mono text-ink-3">{s.turnNumber}</td>
                  <td className="py-1.5 pr-3 text-ink-1">{typeLabel(s.stepType)}</td>
                  <td className="py-1.5 pr-3 font-mono text-ink-1">{s.actionKind ?? "—"}</td>
                  <td className={`py-1.5 ${s.errorMessage ? "text-status-failed" : "text-ink-3"}`}>
                    {flags || "—"}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        <p className="mt-3 text-xs text-ink-3">
          Typed text, selectors and error text are not shown here. They are in the replay.
        </p>
      </>
    );
  }

  return (
    <Panel title="Steps" aside={query.data ? `${steps.length} recorded` : undefined}>
      {body}
    </Panel>
  );
}
